import { Visit } from "../../../system/AppState/AppVisitsState";
import { VisitBroadcastId } from "../../../messages";
import { useState } from "react";
import { Duration, Instant } from "@js-joda/core";
import { isDefined } from "../../Generic/IsDefined";
import { undefinedToNull } from "../../../utils/undefinedToNull";
import { List } from "../../../utils/List";

interface Props {
  visits: List<Visit.Available>;
  onConfirmAssignmentClick: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
}

export default function usePendingConfirmationBannerProps(props: Props) {
  const [timeFilter, setTimeFilter] = useState(Instant.now());

  const pendingConfirmationVisits = getPendingConfirmationVisits(props.visits, timeFilter).sortBy(
    (v) => v.publicVisitBroadcastDetails.startTimeLocal
  );

  const onShowDetailsClick = (visitId: VisitBroadcastId, isReviewOnly: boolean) => {
    const visit = pendingConfirmationVisits.filter(({ id }) => id === visitId).first();
    if (isDefined(visit)) {
      props.onConfirmAssignmentClick(visit.id, isReviewOnly);
    }
  };

  if (pendingConfirmationVisits.length() === 0) {
    return null;
  }

  const currVisit = pendingConfirmationVisits.first();

  if (
    currVisit === undefined ||
    !(
      isDefined(currVisit.caregiverVisitEngagementDetails.pendingConfirmationInstances) &&
      isDefined(currVisit.caregiverVisitEngagementDetails.pendingConfirmationUntil)
    )
  ) {
    return null;
  }

  return {
    visitId: currVisit.id,
    pendingConfirmationUntil: currVisit.caregiverVisitEngagementDetails.pendingConfirmationUntil,

    pendingConfirmationConfirmedAt: undefinedToNull(
      currVisit.caregiverVisitEngagementDetails.pendingConfirmationConfirmedAt
    ),
    onShowDetailsClick,
    onTimerEnd: () => setTimeFilter(Instant.now()),
  };
}

const getPendingConfirmationVisits = (visits: List<Visit.Available>, now: Instant) => {
  return visits.filter((visit) => {
    const { pendingConfirmationInstances, pendingConfirmationShifts, pendingConfirmationUntil } =
      visit.caregiverVisitEngagementDetails;

    const hasPendingConfirmationInstances =
      isDefined(pendingConfirmationInstances) && pendingConfirmationInstances.length > 0;

    const hasPendingConfirmationShifts =
      isDefined(pendingConfirmationShifts) && pendingConfirmationShifts.length > 0;

    const hasPendingConfirmationUntil =
      isDefined(pendingConfirmationUntil) &&
      !Duration.between(now, pendingConfirmationUntil).isNegative();

    return (
      (hasPendingConfirmationInstances || hasPendingConfirmationShifts) &&
      hasPendingConfirmationUntil
    );
  });
};
